// Import from libraries
import { useState, JSX } from 'react'
import { Table as BootstrapTable } from 'react-bootstrap'
import {
    ColumnDef,
    ColumnFiltersState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    Row,
    RowData,
    SortingState,
    TableOptions,
    useReactTable,
    Table as TableModel
} from '@tanstack/react-table'

// Import from scripts
import { Filter } from '@/components/table'
import { joinClasses } from '@/utils/styling'

// Import components
import Loader from '@/components/shared/Loader'


// Extend the ColumnMeta interface via declaration merging to include custom metadata
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string,
    cellClassName?: string,
    filterVariant?: 'range' | 'select',
  }
}

type TableProps<TData extends RowData> = {
    data?: TData[];
    columns: ColumnDef<TData, any>[];
    enableColumnFilters?: boolean;
    columnFilters?: ColumnFiltersState; // Optional external column filters state
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>; // Optional external column filters state
    loading?: boolean;
    error?: Error | null;
    noItemsMessage?: string | React.ReactNode;
    rowClickFn?: (row: Row<TData>) => void;
    initialPageSize?: number;
    maxHeight?: number; // Units: pixels; Note: If maxHeight is set, classes are applied to create sticky headers
    // initialSortingState?: SortingState | undefined;
}

export default function Table<TData extends RowData>({
    data = [],
    columns,
    enableColumnFilters = true,
    columnFilters: externalFilters,
    onColumnFiltersChange: externalSetFilters,
    loading = false,
    error = null,
    noItemsMessage = "No items to show",
    rowClickFn,
    initialPageSize = 10,
    maxHeight,
    // initialSortingState = undefined
}: TableProps<TData>) {
    const [internalFilters, setInternalFilters] = useState<ColumnFiltersState>([]);

    const columnFilters = externalFilters ?? internalFilters;
    const setColumnFilters = externalSetFilters ?? setInternalFilters;

    const tableOptions: TableOptions<TData> = {
        data,
        columns,
        state: {
            columnFilters,
            pagination: {
                pageIndex: 0,
                pageSize: initialPageSize,
            },
            // sorting: initialSortingState,
        },
        defaultColumn: {
            size: 0, // implemented in UI in em units
            minSize: 0, // implemented in UI in em units
        },
        enableColumnFilters: enableColumnFilters,

        // Functions
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(), //client-side filtering
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(), // client-side faceting
        getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
        getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter

        // // Developer tools
        // debugTable: false,
        // debugHeaders: false,
        // debugColumns: false,
    };

    const table = useReactTable<TData>(tableOptions);

    // const setColumnFilterValue = (id: string, value: string | undefined) => {
    //     setColumnFilters(prev =>
    //         value === undefined
    //             ? prev.filter(f => f.id !== id)
    //             : [...prev.filter(f => f.id !== id), { id, value }]
    //     );
    // };

    return (
        <div 
            className={joinClasses("table-responsive", maxHeight ? 'overflow-auto' : '')} 
            style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}
        >
            <table 
                className={joinClasses(
                    "table", 
                    rowClickFn ? 'table-hover' : '', 
                    maxHeight ? 'position-relative' : ''
                )}
            >
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        className={joinClasses(
                                            maxHeight ? 'sticky-top' : '',
                                            header.column.columnDef.meta?.headerClassName
                                        )}
                                        style={{
                                            width: header.getSize() ? `${header.getSize()}em` : ''
                                        }}
                                        aria-sort={
                                            header.column.getCanSort() &&
                                            header.column.getIsSorted() &&
                                            header.column.getSortIndex() === 0
                                                ? header.column.getIsSorted() === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : undefined
                                        }
                                    >
                                        {header.isPlaceholder ? null : (
                                            <>
                                                {header.column.getCanSort() ? (
                                                    <button
                                                        type="button"
                                                        className="button-no-styles select-none"
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        <span aria-hidden="true">
                                                            {{
                                                                asc: ' 🔼',
                                                                desc: ' 🔽',
                                                            }[header.column.getIsSorted() as string] ?? null}
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <div>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </div>
                                                )}
                                                {header.column.getCanFilter() && (
                                                    <div>
                                                        <Filter column={header.column} />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody className="cursor-default">
                    {loading ? (
                        <tr>
                            <td colSpan={columns.length}>
                                <Loader containerClass="py-3" />
                            </td>
                        </tr>
                    ) : error ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center text-danger">
                                Error: {error.message}
                            </td>
                        </tr>
                    ) : table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                {noItemsMessage}
                            </td>
                        </tr>
                    ) : table.getRowModel().rows.map(row => {
                        return (
                            <tr 
                                key={row.id} 
                                onClick={rowClickFn ? () => { rowClickFn(row) } : undefined }
                            >
                                {row.getVisibleCells().map(cell => {
                                    return (
                                        <td 
                                            key={cell.id} 
                                            className={joinClasses(cell.column.columnDef.meta?.cellClassName)}
                                            style={{ 
                                                width: cell.column.getSize() ? `${cell.column.getSize()}em` : '' 
                                            }}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            <div className="h-2" />
            {/* <TablePagination table={table} /> */}
            {/*<div>{table.getPrePaginationRowModel().rows.length} Rows</div>*/}
            {/*<div>*/}
            {/*    <button onClick={() => rerender()}>Force Rerender</button>*/}
            {/*</div>*/}
            {/*  <pre>*/}
            {/*    {JSON.stringify(*/}
            {/*      { columnFilters: table.getState().columnFilters },*/}
            {/*      null,*/}
            {/*      2*/}
            {/*    )}*/}
            {/*  </pre>*/}
        </div>
    )
};

function TablePagination<TData extends RowData>({ table }: { table: TableModel<TData> }) {
    return (
        <>
            {table.getPageCount() > 1 &&
                <div className="flex items-center gap-2">
                    <button className="border rounded p-1" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                        {'<<'}
                    </button>
                    <button className="border rounded p-1" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                        {'<'}
                    </button>
                    <button className="border rounded p-1" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                        {'>'}
                    </button>
                    <button className="border rounded p-1" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
                        {'>>'}
                    </button>
                    <span className="flex items-center gap-1">
                        <div>Page</div>
                        <strong>
                            {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
                        </strong>
                    </span>
                    <span className="flex items-center gap-1">
                        | Go to page:
                        <input
                            type="number"
                            defaultValue={table.getState().pagination.pageIndex + 1}
                            onChange={e => { const page = e.target.value ? Number(e.target.value) - 1 : 0; table.setPageIndex(page) }}
                            className="border p-1 rounded w-16"
                        />
                    </span>
                    <select value={table.getState().pagination.pageSize} onChange={e => { table.setPageSize(Number(e.target.value)) }}>
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            }
        </>
    )
}